import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { GrClose } from "react-icons/gr";
import { VendorItemDetails } from "types/vendor";
import styles from "./HeritageLinkConfirmModal.module.scss";

export type HeritageUnlinkConfirmRequest = {
  localItem: VendorItemDetails;
  heritagePartNumber: string;
  heritageDescription?: string | null;
};

type Props = {
  request: HeritageUnlinkConfirmRequest;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const HeritageUnlinkConfirmModal = ({
  request,
  isSubmitting = false,
  onClose,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const tBase = "views.vendors.VendorBody.VendorItems.VendorItemLinkModal.VendorItemLinkModalBody";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const modal = (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="heritage-unlink-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className="min-w-0 pr-3">
            <h2 id="heritage-unlink-confirm-title" className={styles.title}>
              {tr("Confirm Heritage unlink")}
            </h2>
            <p className={styles.subtitle}>{tr("Confirm Heritage unlink subtitle")}</p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={tr("Close")}
            disabled={isSubmitting}
          >
            <GrClose size={18} />
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.warning} role="alert">
            {tr("Confirm Heritage unlink warning")}
          </p>

          <div className={styles.linkCard}>
            <span className={styles.linkLabel}>{tr("Local item")}</span>
            <span className={styles.linkPart}>
              {request.localItem.item_name || getItemDisplayName(request.localItem)}
            </span>
            {(request.localItem.model || request.localItem.sku) && (
              <p className={styles.localMeta}>
                {[request.localItem.model, request.localItem.sku].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>

          <div className={styles.linkCard}>
            <span className={styles.linkLabel}>{tr("Current Heritage link")}</span>
            <span className={styles.linkPart}>{request.heritagePartNumber}</span>
            {request.heritageDescription && (
              <span className={styles.linkDescription}>{request.heritageDescription}</span>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onClose}
            disabled={isSubmitting}
          >
            {tr("Cancel")}
          </button>
          <button
            type="button"
            className={`${styles.confirmButton} ${styles.dangerButton}`}
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? tr("Unlinking...") : tr("Confirm unlink")}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

function getItemDisplayName(item: VendorItemDetails): string {
  return (
    item.vendor_part_number ||
    item.item_name ||
    item.short_description ||
    item.model ||
    item.sku ||
    (item.item_id != null ? `Item ${item.item_id}` : "Untitled")
  );
}

export default HeritageUnlinkConfirmModal;
